import axios from "axios";
import demoSnapshot from "../data/demoSnapshot.json";

const IS_DEMO =
  process.env.NODE_ENV === "production" && !process.env.REACT_APP_API_URL;

let demoState = IS_DEMO ? JSON.parse(JSON.stringify(demoSnapshot)) : null;

const demoOk = () => demoState;

const demoUnsupported = (feature) => {
  const err = new Error(
    `${feature} is unavailable in the hosted demo — it requires the local backend.`
  );
  err.isDemoUnsupported = true;
  return err;
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const parseFormulaWithBackend = async ({ inputText, imageBase64 }) => {
  if (IS_DEMO) throw demoUnsupported("Formula parsing");
  const response = await axios.post("/api/parse-formula", {
    inputText,
    imageBase64,
  });

  return response.data;
};

export const getBackendHealth = async () => {
  if (IS_DEMO) return { ok: true, demo: true, persistence: "in-memory" };
  const response = await axios.get("/api/health");
  return response.data;
};

export const getPlatformState = async () => {
  if (IS_DEMO) return demoOk();
  const response = await axios.get("/api/platform-state");
  return response.data;
};

export const saveFormulaRun = async (payload) => {
  if (IS_DEMO) throw demoUnsupported("Saving formula runs");
  const response = await axios.post("/api/formula-runs", payload);
  return response.data;
};

export const uploadDocuments = async ({ files, type, productId, vendorId, owner }) => {
  const encodedFiles = await Promise.all(
    (files || []).map(async (file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      contentBase64: await fileToBase64(file),
    }))
  );

  if (IS_DEMO) throw demoUnsupported("Document uploads");
  const response = await axios.post("/api/documents/upload", {
    files: encodedFiles,
    type,
    productId,
    vendorId,
    owner,
  });

  return response.data;
};

export const createVendor = async (payload) => {
  if (IS_DEMO) throw demoUnsupported("Creating vendors");
  const response = await axios.post("/api/vendors", payload);
  return response.data;
};

export const createVendorCertification = async ({ vendorId, certification }) => {
  if (IS_DEMO) throw demoUnsupported("Adding vendor certifications");
  const response = await axios.post(`/api/vendors/${vendorId}/certifications`, certification);
  return response.data;
};

export const updateJobStatus = async ({ jobId, status }) => {
  if (IS_DEMO) return demoOk();
  const response = await axios.patch(`/api/jobs/${jobId}`, { status });
  return response.data;
};

export const syncRegulatorySource = async (sourceType) => {
  if (IS_DEMO) return demoOk();
  const response = await axios.post(`/api/regulatory/sync/${sourceType}`);
  return response.data;
};

export const syncAllRegulatorySources = async () => {
  if (IS_DEMO) return demoOk();
  const response = await axios.post("/api/regulatory/sync-all");
  return response.data;
};

export const recomputeRegulatoryImpacts = async () => {
  if (IS_DEMO) return demoOk();
  const response = await axios.post("/api/regulatory/recompute-impacts");
  return response.data;
};

export const updateVendorFacility = async ({ vendorId, facility }) => {
  if (IS_DEMO) return demoOk();
  const response = await axios.patch(`/api/vendors/${vendorId}/facility`, facility);
  return response.data;
};

export const updateProductMocra = async ({ productId, mocra }) => {
  if (IS_DEMO) return demoOk();
  const response = await axios.patch(`/api/products/${productId}/mocra`, mocra);
  return response.data;
};

export const addAdverseEvent = async ({ productId, event }) => {
  if (IS_DEMO) throw demoUnsupported("Recording adverse events");
  const response = await axios.post(`/api/products/${productId}/adverse-events`, event);
  return response.data;
};

export const updateAdverseEvent = async ({ productId, eventId, updates }) => {
  if (IS_DEMO) throw demoUnsupported("Updating adverse events");
  const response = await axios.patch(`/api/products/${productId}/adverse-events/${eventId}`, updates);
  return response.data;
};

const POLL_INTERVAL_MS = 1500;

const buildSyncStreamUrl = (sources) => {
  const base = "/api/regulatory/sync-stream";
  if (!sources || (Array.isArray(sources) && sources.length === 0)) {
    return base;
  }
  const list = Array.isArray(sources) ? sources : [sources];
  const encoded = list
    .filter((value) => value !== undefined && value !== null && value !== "")
    .map((value) => encodeURIComponent(String(value)))
    .join(",");
  return encoded ? `${base}?sources=${encoded}` : base;
};

const safeInvoke = (fn, ...args) => {
  if (typeof fn !== "function") return undefined;
  try {
    return fn(...args);
  } catch (invokeError) {
    // Swallow listener errors so the subscription stays alive.
    return undefined;
  }
};

const parseEventData = (raw) => {
  if (raw == null) return null;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (parseError) {
    return { raw };
  }
};

export const subscribeSyncStream = ({ sources, onEvent, onError, onComplete } = {}) => {
  if (IS_DEMO) {
    setTimeout(() => {
      safeInvoke(onEvent, { phase: "complete", demo: true });
      safeInvoke(onComplete);
    }, 0);
    return { close: () => {} };
  }
  const url = buildSyncStreamUrl(sources);
  const hasEventSource =
    typeof window !== "undefined" && typeof window.EventSource === "function";

  if (hasEventSource) {
    let source;
    try {
      source = new window.EventSource(url);
    } catch (constructError) {
      safeInvoke(onError, constructError);
      return { close: () => {} };
    }

    source.onmessage = (event) => {
      const payload = parseEventData(event?.data);
      safeInvoke(onEvent, payload, event);
    };
    source.onerror = (event) => {
      safeInvoke(onError, event);
    };
    if (typeof source.addEventListener === "function") {
      source.addEventListener("complete", () => {
        safeInvoke(onComplete);
        try {
          source.close();
        } catch (closeError) {
          // ignore
        }
      });
    }

    return {
      close: () => {
        try {
          source.close();
        } catch (closeError) {
          // ignore
        }
      },
    };
  }

  // Fallback: poll the progress endpoint.
  let cancelled = false;
  let timer = null;
  let lastSeen = null;

  const stop = () => {
    cancelled = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const tick = async () => {
    if (cancelled) return;
    try {
      const response = await axios.get("/api/regulatory/sync-progress");
      const payload = response?.data;
      const signature = JSON.stringify(payload ?? null);
      if (signature !== lastSeen) {
        lastSeen = signature;
        safeInvoke(onEvent, payload);
      }
      if (payload && payload.phase === "complete") {
        safeInvoke(onComplete);
        stop();
        return;
      }
    } catch (pollError) {
      if (pollError?.response?.status === 404) {
        // Endpoint missing - degrade to no-op.
        stop();
        return;
      }
      safeInvoke(onError, pollError);
    }
    if (!cancelled) {
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    }
  };

  // Kick off asynchronously so callers can wire up state first.
  timer = setTimeout(tick, 0);

  return { close: stop };
};
